import type React from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import type { CompanyByIndustry } from '@xing-com/crate-common-graphql-types';

import { SubpageLayout } from '../../components/layouts/subpage-layout';
import { NewPagination } from '../../components/new-pagination/new-pagination';
import { SimpleCompanyCardGrid } from '../../components/simple-company-card-grid/simple-company-card-grid';
import { useCompaniesByIndustry } from '../../hooks/use-companies-by-industry';
import { usePagination } from '../../hooks/use-pagination';
import { useTrackPage } from '../../hooks/use-track-page';
import { createSimpleCompanies } from '../../models/most-visited-companies';
import type { SimpleCompanyItem } from '../../types';
import { pagesTrackingName } from '../../utils/tracking';
import { getIndustryName } from '../../utils/url';

const LIMIT = 24;

type IndustryParams = {
  industry: string;
};

export const NewIndustryCompanies: React.FC = () => {
  const intl = useIntl();
  const { industry } = useParams<IndustryParams>();
  const { changePage, activePage } = usePagination();

  const [totalCompanies, setTotalCompanies] = useState(0);
  const id = industry.split('-')[0];

  const { data, loading } = useCompaniesByIndustry({
    id,
    limit: LIMIT,
    offset: LIMIT * (activePage - 1),
  });

  useEffect(() => {
    const total = data?.companiesByIndustry?.total ?? 0;

    if (!loading && totalCompanies !== total) {
      setTotalCompanies(total);
    }
  }, [loading]);

  let companiesByIndustry: SimpleCompanyItem[] = [];

  if (data?.companiesByIndustry?.collection) {
    const companies = data.companiesByIndustry
      .collection as CompanyByIndustry[];
    companiesByIndustry = createSimpleCompanies(companies);
  }

  const industryName = getIndustryName({ id, intl });

  useTrackPage({ pageName: pagesTrackingName.industry });

  return (
    <SubpageLayout
      headerText={intl.formatMessage(
        { id: 'CP_100_DIRECTORY_TITLE_INDUSTRY' },
        { industry: industryName }
      )}
      description={intl.$t(
        { id: 'CP_100_DIRECTORY_INTRO_INDUSTRY' },
        { industry: industryName }
      )}
    >
      {companiesByIndustry && (
        <SimpleCompanyCardGrid
          items={companiesByIndustry}
          limit={LIMIT}
          loading={loading}
        />
      )}
      {companiesByIndustry && (
        <NewPagination
          currentPage={activePage}
          perPage={LIMIT}
          total={totalCompanies}
          onPageClick={changePage}
        />
      )}
    </SubpageLayout>
  );
};
